import { useState } from "react";
import { useTranslation } from "react-i18next";

import ConfirmModal from "../../../../../../components/ConfirmModal";
import { EmptyState } from "../../../../../../components/EmptyState";
import { GlobeIcon } from "../../../../../../icons/Globe";
import { OwnedDomain } from "../../../../../../services/domains";
import { Skeleton, SmallButton } from "../../../../../Domains/styles";
import { DomainChoices } from "../../styles";

type Props = {
  /** The person's domains bought at Keepo that can be connected; undefined while loading. */
  domains?: OwnedDomain[];
  /** This Keepo: a domain on another one is moved here, asked first. */
  siteId: string;
  /** The domain being connected now (its button loads, the others wait). */
  connecting?: string;
  onConnect: (domain: string) => void;
  onBuy: () => void;
};

/**
 * The domains bought at Keepo, right in the box, each with Connect (Keepo sets the DNS by itself, so that's all
 * there is). One on another Keepo asks before moving it here. Without any, it offers to buy one.
 */
export const KeepoDomains = ({ domains, siteId, connecting, onConnect, onBuy }: Props) => {
  const { t } = useTranslation();
  const prefix = "pages.settings.customDomain.keepoDomain";
  // A domain on another Keepo, waiting for the person to say it can move here
  const [moving, setMoving] = useState<OwnedDomain>();

  const otherKeepo = (item: OwnedDomain) => (item.keepo && item.keepo.siteId !== siteId ? item.keepo : null);

  const connect = (item: OwnedDomain) => (otherKeepo(item) ? setMoving(item) : onConnect(item.domain));

  return (
    <DomainChoices aria-busy={!domains || !!connecting}>
      <header>
        <h3>{t(`${prefix}.list.title`)}</h3>
        <p>{t(`${prefix}.list.text`)}</p>
      </header>

      {!domains ? (
        // Loading: the rows' shape pulsing, as the other lists load
        <ul aria-hidden="true">
          {[0, 1].map(row => (
            <li key={row}>
              <Skeleton className="row-icon" />
              <span className="text">
                <Skeleton className="name" />
                <Skeleton className="meta" />
              </span>
            </li>
          ))}
        </ul>
      ) : domains.length ? (
        <ul>
          {domains.map(item => {
            const other = otherKeepo(item);
            return (
              <li key={item.domain}>
                <span className="icon" aria-hidden="true">
                  <GlobeIcon size={18} />
                </span>
                <span className="text">
                  <span className="name">
                    <b>{item.domain}</b>
                  </span>
                  {/* as on /domains: a colored dot and its words, free to connect or on which Keepo */}
                  <span className={other ? "status connected" : "status free"}>
                    {other
                      ? t(`${prefix}.list.connectedTo`, { keepo: `${other.url}.keepo.bio` })
                      : t(`${prefix}.list.tagFree`)}
                  </span>
                </span>
                <SmallButton
                  type="button"
                  aria-label={t(`${prefix}.list.connectDomain`, { domain: item.domain })}
                  disabled={!!connecting}
                  $loading={connecting === item.domain}
                  onClick={() => connect(item)}>
                  {t(`${prefix}.list.connect`)}
                </SmallButton>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="empty-state">
          <EmptyState
            emoji="🌐"
            title={t(`${prefix}.list.emptyTitle`)}
            description={t(`${prefix}.list.emptyText`)}
            action={{ label: t(`${prefix}.list.buy`), onClick: onBuy }}
          />
        </div>
      )}

      {moving && (
        <ConfirmModal
          type="INFO"
          title={t(`${prefix}.move.title`, { domain: moving.domain })}
          descriptions={[t(`${prefix}.move.text`, { keepo: `${otherKeepo(moving)?.url}.keepo.bio` })]}
          confirmButtonTitle={t(`${prefix}.move.confirm`)}
          cancelTitle={t(`${prefix}.move.cancel`)}
          onConfirm={() => {
            onConnect(moving.domain);
            setMoving(undefined);
          }}
          onCancel={() => setMoving(undefined)}
          onExit={() => setMoving(undefined)}
        />
      )}
    </DomainChoices>
  );
};
